import * as React from 'react';
import { connect } from 'react-redux';
import classnames from 'classnames';
import isNil from 'lodash/isNil';

import Input from 'components/inputs/input/Input';
import { FormField } from 'components/form';
import Switch from 'components/switch/Switch';
import Tooltip from 'components/Tooltip';
import SVGIcon from 'components/svg-icon/SVGIcon';
import FacebookButton from 'components/external-integration/facebook/button/FacebookButton';
import FacebookButtonStatusMessage from 'components/external-integration/facebook/button/common/status-message/FacebookButtonStatusMessage';
import FacebookButtonErrorMessage from 'components/external-integration/facebook/button/common/error-message/FacebookButtonErrorMessage';
import FacebookButtonNoConnect from 'components/external-integration/facebook/button/common/no-connect/FacebookButtonNoConnect';

import ChannelAudienceDataImage from '../channel-audience-data-image/ChannelAudienceDataImage';

import { __t, applyTranslations } from 'utils/language';
import { isChannelUrl, isRequired } from 'utils/validators';
import { addProtocol } from 'utils/url';
import {
  AUDIENCE_DATA_INFLUENCER_CHANNELS,
  INFLUENCER_CHANNELS,
  SUPPORTED_INFLUENCER_CHANNELS,
  getChannelValue,
  getFullChannelUrl
} from 'utils/influencers';
import { CHANNEL_TYPE_INSTAGRAM, CHANNEL_TYPE_TIKTOK } from 'utils/channels';

import type { ChannelAnalyticsScreenShotData, ChannelForm, TextEntry } from 'types';
import type { ChannelSwitchExpiredAnalytics } from './types';
import type {
  FacebookButtonState
} from 'components/external-integration/facebook/button/types';

applyTranslations(require('locales/common/collabary-channels.i18n.json'));
applyTranslations(require('./channel-switch.i18n.json'));

type OwnProps = {
  channel: ChannelForm,
  isAdmin?: boolean,
  isCreationStep?: boolean,
  hasError: boolean,
  errorMessage?: string | null,
  expiredAnalyticsDataForInfluencer: ChannelSwitchExpiredAnalytics,
  onSuccessfulFacebookLogin?: () => void,
  onChange: (a: ChannelForm, isToggled?: boolean) => void
};

type StateProps = {
  facebookButtonState: FacebookButtonState
}

type Props = OwnProps & StateProps;

type State = {
  channel: ChannelForm,
  isSwitchedOn: boolean
};

class ChannelSwitch extends React.PureComponent<Props, State> {
  state = this.getDefaultState(this.props);

  componentDidUpdate(prevProps: Props, prevState: State) {
    const { channel } = this.props;

    if (!prevState.channel.url && channel.url) {
      // on edit page channels data gets available later
      this.setState(this.getDefaultState(this.props)); // eslint-disable-line react/no-did-update-set-state
    }
  }

  getDefaultState(nextProps: Props): State {
    const isSwitchedOn = Boolean(nextProps.channel.url) && nextProps.channel.url.length > 0;

    return {
      channel: {
        ...nextProps.channel
      },
      isSwitchedOn
    };
  }

  getHasConnectedFacebookStatus(): boolean {
    const { facebookButtonState } = this.props;

    return facebookButtonState.isFBLoginActive && !facebookButtonState.isFBLoginExpired;
  }

  isInstagramChannelConnectedToFb(): boolean {
    const { channel } = this.props;
    return channel.type === CHANNEL_TYPE_INSTAGRAM && this.getHasConnectedFacebookStatus();
  }

  handleToggle = (isSwitchedOn: boolean): void => {
    const { onChange, channel: originalChannel } = this.props;
    const { channel } = this.state;

    this.setState({
      isSwitchedOn
    });

    const updatedChannel: ChannelForm = {
      ...originalChannel,
      url: isSwitchedOn ? channel.url : ''
    };

    onChange(updatedChannel, true);
  };

  handleUrlChanged = (channelName?: string) => {
    const { onChange, channel: originalChannel } = this.props;
    const { channel } = this.state;
    const url = (channelName || '').trim();
    const fullUrl = getFullChannelUrl(channel.type, url);

    this.setState({
      channel: {
        ...channel,
        url: fullUrl
      }
    });

    const updatedChannel: ChannelForm = {
      ...originalChannel,
      url: fullUrl,
      isInvalid: false
    };

    onChange(updatedChannel);
  };

  handleAudienceDataImageChange = (assets: Array<ChannelAnalyticsScreenShotData>) => {
    const { onChange, channel: originalChannel } = this.props;
    const { channel } = this.state;

    this.setState({
      channel: {
        ...channel,
        assets
      }
    });

    onChange({
      ...originalChannel,
      assets
    });
  };

  handleSuccessfulFacebookLogin = (): void => {
    const { onSuccessfulFacebookLogin, facebookButtonState } = this.props;
    const instagramUserName: string = facebookButtonState.instagramUserName ?? '';

    if (instagramUserName && this.isInstagramChannelConnectedToFb()) {
      this.handleUrlChanged(instagramUserName);
    }

    if (onSuccessfulFacebookLogin) {
      onSuccessfulFacebookLogin();
    }
  };

  renderChannelAudienceDataImage = (): React.ReactNode => {
    const {
      channel,
      isAdmin,
      isCreationStep,
      expiredAnalyticsDataForInfluencer
    } = this.props;

    return AUDIENCE_DATA_INFLUENCER_CHANNELS.includes(channel.type)
      && expiredAnalyticsDataForInfluencer[channel.type]
      && (
        <ChannelAudienceDataImage
          channel={channel}
          isAdmin={isAdmin}
          isCreationStep={isCreationStep}
          hasConnectedFacebookOverride={this.isInstagramChannelConnectedToFb()}
          onAudienceDataImageChange={this.handleAudienceDataImageChange}
        />
      );
  };

  renderNotLoggedInToFacebookSection = (): React.ReactNode => {
    const { expiredAnalyticsDataForInfluencer, channel, hasError } = this.props;

    if (!expiredAnalyticsDataForInfluencer[channel.type]) {
      return null;
    }

    return (
      <div className="channel-switch__footer-no-connect">
        <FacebookButtonNoConnect
          toggleOverride={hasError ? 'expanded' : 'collapsed'}
        >
          {this.renderChannelAudienceDataImage()}
        </FacebookButtonNoConnect>
      </div>
    );
  };

  renderFacebookButton = (): React.ReactNode => {
    const { isCreationStep, facebookButtonState } = this.props;

    return (
      <div className="channel-switch__facebook-button">
        <FacebookButtonStatusMessage
          isLoggedIn={facebookButtonState.isFBLoginActive}
          isCentered
        />
        <FacebookButton
          isCreationStep={isCreationStep}
          onSuccessfulLogin={this.handleSuccessfulFacebookLogin}
          isCentered
        />
        {
          !isNil(facebookButtonState.error) && (
            <FacebookButtonErrorMessage
              message={facebookButtonState.error}
              isCentered
            />
          )
        }
      </div>
    );
  };

  renderFooter() {
    const { errorMessage, isCreationStep, facebookButtonState } = this.props;
    const { channel } = this.state;
    let instagramUserName: string = getChannelValue(channel) || '';

    const footerClasses = classnames({
      'channel-switch__footer': true,
      [`channel-switch__footer--channel-${channel.type}`]: channel.type
    });

    const hintText = INFLUENCER_CHANNELS[channel.type].short;

    if (this.isInstagramChannelConnectedToFb()) {
      instagramUserName = facebookButtonState.instagramUserName ?? '';
    }

    const updatedChannelUrl = addProtocol(channel.url, 'https');

    return (
      <div className={footerClasses}>
        <FormField
          direction="column"
          showValidationIcon={false}
          validators={[
            isRequired(channel.url),
            isChannelUrl(channel.type, updatedChannelUrl, __t(`collabary_channels.${channel.type}.example`))
          ]}
          immediateValidationError={errorMessage || ''}
        >
          {this.isInstagramChannelConnectedToFb() && (
            <div className="channel-switch__footer-input--disabled">
              {`${hintText}${instagramUserName}`}
              <Tooltip
                classNameWrapper="channel-switch__footer-input-tooltip"
                message={__t('channel_switch.instagram_disabled_tooltip')}
                width="normal"
                showQuestionMark
              />
            </div>
          )}
          {!this.isInstagramChannelConnectedToFb() && (
            <Input
              width="responsive"
              value={instagramUserName}
              hint={hintText}
              placeholder={__t(`collabary_channels.${channel.type}.placeholder`)}
              onChange={(event: React.SyntheticEvent<TextEntry>) => this.handleUrlChanged(event.currentTarget.value)}
            />
          )}
        </FormField>
        {
          // edit profile
          (channel.type === CHANNEL_TYPE_TIKTOK) && !isCreationStep &&
            this.renderChannelAudienceDataImage()
        }
        {
          // create profile
          (channel.type === CHANNEL_TYPE_TIKTOK) && isCreationStep &&
            this.renderChannelAudienceDataImage()
        }
        {
          channel.type === CHANNEL_TYPE_INSTAGRAM
            && (
              <React.Fragment>
                {this.renderFacebookButton()}
                {
                  !this.getHasConnectedFacebookStatus()
                    && this.renderNotLoggedInToFacebookSection()
                }
              </React.Fragment>
            )
        }
      </div>
    );
  }

  render() {
    const { channel, isSwitchedOn } = this.state;
    const isSwitchedOnOrDisabled = isSwitchedOn || this.isInstagramChannelConnectedToFb();
    return (
      <section className="channel-switch">
        <div className="channel-switch__header">
          <div className="channel-switch__header-info">
            <div className="channel-switch__header-icon">
              <SVGIcon
                name={`${channel.type}-no-customisation`}
                width={48}
                height={48}
              />
            </div>
            <div>
              <div className="channel-switch__header-title">
                {__t(`collabary_channels.${channel.type}.label`)}
              </div>
              {
                SUPPORTED_INFLUENCER_CHANNELS.includes(channel.type) &&
                  <div className="channel-switch__header-subtitle">
                    {__t('channel_switch.supported_channel')}
                  </div>
              }
            </div>
          </div>
          <div className="channel-switch__header-action">
            <Switch
              value={isSwitchedOnOrDisabled}
              onChange={this.handleToggle}
              isEditable={!this.isInstagramChannelConnectedToFb()}
            />
          </div>
        </div>
        {
          (isSwitchedOnOrDisabled) &&
            this.renderFooter()
        }
      </section>
    );
  }
}

const mapStateToProps = ({ facebookButton }): StateProps => ({
  facebookButtonState: facebookButton
});

export default connect(mapStateToProps)(ChannelSwitch);
